import React, { useState, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Code2, Wrench, Database, ArrowRight, Lightbulb, ArrowDown, Upload, FileUp, X, CheckCircle, AlertCircle } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';

const ToolsIndex = () => {
  const { token } = useContext(AuthContext);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const importSources = [
    { id: 'aspire', name: 'Vectric Aspire', extension: '.tools', description: 'Aspire tool database export' },
    { id: 'vcarve', name: 'Vectric VCarve', extension: '.tools', description: 'VCarve tool database export' },
    { id: 'fusion360', name: 'Fusion 360', extension: '.json', description: 'Fusion 360 tool library JSON export' },
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !selectedSource) {
      toast.error('Please select a source and file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source', selectedSource);

      const response = await axios.post(`${API}/user-tools/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} tool(s)!`);
      } else if (response.data.skipped_count > 0) {
        toast.info(`${response.data.skipped_count} tool(s) already exist and were skipped.`);
      } else if (response.data.errors.length > 0) {
        toast.error(response.data.errors[0]);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import tools');
      setImportResult({ success: false, errors: [error.response?.data?.detail || 'Import failed'] });
    } finally {
      setImporting(false);
    }
  };

  const resetImportModal = () => {
    setSelectedSource('');
    setSelectedFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const tools = [
    {
      id: 'quick-pick',
      title: 'Quick Pick',
      description: 'Select your project type to get instant material and tool recommendations. Perfect for getting started!',
      icon: Lightbulb,
      link: '/tools/quick-pick',
      cta: 'Find Recommendations',
      featured: true
    },
    {
      id: 'calculator',
      title: 'CNC Calculator',
      description: 'Feeds & speeds calculator with safety-focused recommendations. Material-aware calculations for perfect cuts.',
      icon: Calculator,
      link: '/tools/feeds-speeds',
      cta: 'Open Calculator'
    },
    {
      id: 'gcode',
      title: 'G-Code Master',
      description: 'Review and optimize G-code based on your machine profile and controller. AI-powered analysis.',
      icon: Code2,
      link: '/tools/gcode-master',
      cta: 'Open G-Code Master'
    },
    {
      id: 'bit-selector',
      title: 'Bit Selector',
      description: 'Find the right tool for your job. Filter by material, operation type, and machine capabilities.',
      icon: Wrench,
      link: '/tools/bit-selector',
      cta: 'Open Bit Selector'
    },
    {
      id: 'material-db',
      title: 'Material Database',
      description: 'Reference library of material properties and recommended cutting parameters.',
      icon: Database,
      link: '/tools/material-db',
      cta: 'Open Material Database'
    },
    {
      id: 'plunge-recommender',
      title: 'Plunge Rate Calculator',
      description: 'Calculate safe plunge rates based on material, tool size, and feed rate. Includes ramping tips.',
      icon: ArrowDown,
      link: '/tools/plunge-recommender',
      cta: 'Calculate Plunge Rate'
    }
  ];

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.MY_TOOLS)}
      data-testid="tools-index-page"
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Workspace</div>
        <h1 
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight uppercase"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          CNC Tools
        </h1>
        <p className="mt-2 text-sm md:text-base text-[#d6d6d6] max-w-lg">
          Pick a module to continue. Use the calculator for feeds & speeds, or optimize G-code for your controller.
        </p>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <div 
              key={tool.id}
              className={`bg-[#242424] border rounded-sm p-6 transition-all ${
                tool.featured 
                  ? 'border-[#e67e22] shadow-[0_0_20px_rgba(230,126,34,0.2)] md:col-span-2'
                  : 'border-[#3a3a3a]'
              } ${
                tool.disabled 
                  ? 'opacity-60' 
                  : 'hover:border-[#e67e22]/50 hover:shadow-[0_0_15px_rgba(230,126,34,0.1)]'
              }`}
            >
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center rounded-sm">
                    <Icon className="w-6 h-6 text-[#e67e22]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{tool.title}</h3>
                    <p className="text-sm text-[#a3a3a3] mt-1">{tool.description}</p>
                  </div>
                </div>
                
                <div className="pt-2">
                  {tool.disabled ? (
                    <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#737373] bg-[#2c2c2c] border border-[#3a3a3a] rounded-sm cursor-not-allowed">
                      {tool.cta}
                    </span>
                  ) : tool.id === 'bit-selector' ? (
                    <div className="flex flex-wrap gap-3">
                      <Link to={tool.link}>
                        <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#e67e22] hover:bg-[#d35400] rounded-sm transition-colors">
                          {tool.cta}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                      <button 
                        onClick={() => setShowImportModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-[#4a4a4a] rounded-sm transition-colors"
                        data-testid="import-tools-btn"
                      >
                        <Upload className="w-4 h-4" />
                        Import your own Tools DB...
                      </button>
                    </div>
                  ) : (
                    <Link to={tool.link}>
                      <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#e67e22] hover:bg-[#d35400] rounded-sm transition-colors">
                        {tool.cta}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Import Tools Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        setShowImportModal(open);
        if (!open) resetImportModal();
      }}>
        <DialogContent className="bg-[#1f1f1f] border-[#3a3a3a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileUp className="w-5 h-5 text-[#e67e22]" />
              Import Tool Database
            </DialogTitle>
            <DialogDescription className="text-[#a3a3a3]">
              Import your tool library from CAM software. Imported tools will be available in your profile and can be used in calculations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-[#a3a3a3]">
                Select Application
              </label>
              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger className="bg-[#2c2c2c] border-[#3a3a3a] text-white" data-testid="import-source-select">
                  <SelectValue placeholder="Choose your CAM software..." />
                </SelectTrigger>
                <SelectContent className="bg-[#2c2c2c] border-[#3a3a3a]">
                  {importSources.map((source) => (
                    <SelectItem key={source.id} value={source.id} className="text-white hover:bg-[#3a3a3a]">
                      <div className="flex flex-col">
                        <span>{source.name}</span>
                        <span className="text-xs text-[#737373]">{source.extension} - {source.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            {selectedSource && (
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-[#a3a3a3]">
                  Select File
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept={importSources.find(s => s.id === selectedSource)?.extension || '*'}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#2c2c2c] border border-dashed border-[#4a4a4a] rounded-sm hover:border-[#e67e22]/50 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-[#a3a3a3]" />
                    <span className="text-[#d6d6d6]">
                      {selectedFile ? selectedFile.name : 'Click to select file...'}
                    </span>
                  </button>
                  {selectedFile && (
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="p-2 text-[#a3a3a3] hover:text-red-400 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <div className={`p-4 rounded-sm border ${
                importResult.success && importResult.imported_count > 0
                  ? 'bg-green-500/10 border-green-500/30'
                  : importResult.errors?.length > 0
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                {importResult.success && importResult.imported_count > 0 ? (
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <div className="font-semibold text-green-400">Import Successful!</div>
                      <p className="text-sm text-green-200/80 mt-1">
                        Imported {importResult.imported_count} tool(s).
                        {importResult.skipped_count > 0 && ` Skipped ${importResult.skipped_count} duplicate(s).`}
                      </p>
                      <p className="text-xs text-[#a3a3a3] mt-2">
                        View your imported tools in Profile → My Tools
                      </p>
                    </div>
                  </div>
                ) : importResult.errors?.length > 0 ? (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <div className="font-semibold text-red-400">Import Failed</div>
                      <p className="text-sm text-red-200/80 mt-1">{importResult.errors[0]}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div>
                      <div className="font-semibold text-yellow-400">No New Tools</div>
                      <p className="text-sm text-yellow-200/80 mt-1">
                        All tools in this file already exist in your library.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-sm p-4">
              <h4 className="text-sm font-semibold text-[#e67e22] mb-2">How to Export from Aspire/VCarve:</h4>
              <ol className="text-xs text-[#a3a3a3] space-y-1 list-decimal list-inside">
                <li>Open Vectric Aspire or VCarve Pro</li>
                <li>Go to Toolpaths → Tool Database</li>
                <li>Right-click on a tool group and select "Export"</li>
                <li>Save as .tools file and upload here</li>
              </ol>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportModal(false);
                resetImportModal();
              }}
              className="border-[#3a3a3a] text-[#d6d6d6] hover:bg-[#2c2c2c]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!selectedFile || !selectedSource || importing}
              className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold disabled:opacity-50"
              data-testid="import-confirm-btn"
            >
              {importing ? 'Importing...' : 'Import Tools'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToolsIndex;
